import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Box, Card, Flex, Skeleton, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { GameDataContext } from 'contexts/game-data.context';
import { GDPlayerSelectorMode } from 'enums/game-data.enums';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { safeNumber } from 'lib_ts/classes/math.utilities';
import { MlbHelper } from 'lib_ts/classes/mlb.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  IMlbPlayer,
  IMlbPlayerCoordinate,
} from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { first, isNil } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';

// for sorting players by team
const NO_TEAM = '--NO TEAM--';

export const GameDataPlayerSelector = (props: {
  mode: GDPlayerSelectorMode;

  model?: Nullable<IMlbPlayerCoordinate>;
  onChangeModel: (value: IMlbPlayer | undefined) => void;

  // whether to close the drawer after selecting a player
  autoClose?: boolean;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const { filterGlobal, loading, seasons, seasonPlayers, mergeFilterGlobal } =
    useContext(GameDataContext);

  const seasonKey = useMemo(() => Date.now(), [seasons]);

  // ensure global filter matches the default values (if any)
  useEffect(() => {
    if (!props.model) {
      // no default
      return;
    }

    if (
      props.model.season === filterGlobal.season &&
      props.model.sportId === filterGlobal.sportId
    ) {
      // no diff
      return;
    }

    mergeFilterGlobal({
      season: props.model.season,
      sportId: props.model.sportId,
    });
  }, []);

  const playerOptions = useMemo(() => {
    const players = (() => {
      switch (props.mode) {
        case GDPlayerSelectorMode.Hitter: {
          return seasonPlayers.filter((p) => p._mx_details?.isBatter);
        }

        case GDPlayerSelectorMode.Pitcher: {
          return seasonPlayers.filter((p) => p._mx_details?.isPitcher);
        }

        case GDPlayerSelectorMode.Player:
        default: {
          return seasonPlayers;
        }
      }
    })();

    players.sort((a, b) => {
      const [aTeam, bTeam] = [a, b].map(
        (m) => m._mx_team_details?.name ?? NO_TEAM
      );

      if (aTeam === bTeam) {
        return StringHelper.safeSort(a.name, b.name);
      }

      // put ppl w/o teams to the end
      if (aTeam === NO_TEAM) {
        return 1;
      }

      if (bTeam === NO_TEAM) {
        return -1;
      }

      // otherwise sort by diff team names
      return StringHelper.safeSort(aTeam, bTeam);
    });

    const suffixFn = (player: IMlbPlayer) => {
      switch (props.mode) {
        case GDPlayerSelectorMode.Pitcher: {
          return <PitcherHandBadge value={player.pitchHand} />;
        }

        case GDPlayerSelectorMode.Hitter: {
          return <HitterSideBadge value={player.batSide} />;
        }

        default: {
          return undefined;
        }
      }
    };

    return players.map((p) => {
      const { _mx_team_details } = p;
      const aliases: string[] = [p.name];

      if (_mx_team_details) {
        aliases.push(
          ...ArrayHelper.unique([
            _mx_team_details.code ?? '',
            _mx_team_details.name ?? '',
            _mx_team_details.shortName ?? '',
          ])
        );
      }

      const o: IOptionExt = {
        label: p.name,
        group: _mx_team_details?.name ?? 'No Active Roster',
        suffix: suffixFn(p),
        aliases,
        value: p.playerPk.toString(),
      };

      return o;
    });
  }, [props.mode, seasonPlayers]);

  const selectedPlayer = useMemo(
    () => seasonPlayers.find((p) => p.playerPk === props.model?.playerPk),
    [seasonPlayers, props.model?.playerPk]
  );

  const label = useMemo(() => {
    switch (props.mode) {
      case GDPlayerSelectorMode.Hitter: {
        return 'common.hitter';
      }

      case GDPlayerSelectorMode.Pitcher: {
        return 'common.pitcher';
      }

      case GDPlayerSelectorMode.Player:
      default: {
        return 'common.player';
      }
    }
  }, [props.mode]);

  return (
    <ErrorBoundary identifier="GameDataPlayerSelector">
      <Card className="no-background" size="1">
        <Flex direction="column">
          <Skeleton loading={loading}>
            <Flex
              gap="2"
              align="center"
              className={
                props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }
              onClick={() => {
                if (props.disabled) {
                  return;
                }

                setOpen((prev) => !prev);
              }}
            >
              <GameDataPlayerAvatar
                size="2"
                playerPk={selectedPlayer?.playerPk}
                name={selectedPlayer?.name}
              />

              <Flex direction="column" flexGrow="1">
                <Text>
                  {selectedPlayer?.name ?? t('common.select-a-player')}
                </Text>

                {selectedPlayer && (
                  <Text color={RADIX.COLOR.NEUTRAL}>
                    {MlbHelper.getLevelFromSportId(selectedPlayer.sportId)}
                    {HINT_DELIMITER}
                    {/* show up to one, never both of the following */}
                    {GDPlayerSelectorMode.Pitcher === props.mode &&
                      selectedPlayer.pitchHand}
                    {GDPlayerSelectorMode.Hitter === props.mode &&
                      selectedPlayer.batSide}
                    {HINT_DELIMITER}
                    {selectedPlayer.season}
                    {HINT_DELIMITER}
                    <GameDataTeamLogo
                      teamPK={selectedPlayer._mx_team_details?.teamPk}
                      size="12px"
                    />{' '}
                    {selectedPlayer._mx_team_details?.shortName}
                  </Text>
                )}
              </Flex>

              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Flex>
          </Skeleton>
          <AnimateHeight
            duration={200}
            height={open ? 'auto' : 0}
            easing="ease-in-out"
          >
            <Box mt="4">
              <CommonFormGrid columns={2}>
                <CommonSelectInput
                  key={`sportId-${seasonKey}`}
                  id="mlb-stats-sportId"
                  name="sportId"
                  label="common.level"
                  options={[
                    { label: 'MLB', value: MlbSportId.MLB.toString() },
                    { label: 'AAA', value: MlbSportId.AAA.toString() },
                  ]}
                  value={filterGlobal.sportId.toString()}
                  onNumericChange={(v) => {
                    if (v === filterGlobal.sportId) {
                      return;
                    }

                    // reset selected player when level changes
                    props.onChangeModel(undefined);

                    mergeFilterGlobal({
                      sportId: v,
                    });
                  }}
                  disabled={loading}
                  skipSort
                />

                <CommonSelectInput
                  key={`season-${seasonKey}`}
                  id="mlb-stats-season"
                  name="season"
                  label="common.season"
                  options={seasons}
                  value={filterGlobal.season?.toString()}
                  onNumericChange={(v) => {
                    if (v === filterGlobal.season) {
                      return;
                    }

                    // reset selected player when season changes
                    props.onChangeModel(undefined);

                    mergeFilterGlobal({
                      season: v,
                    });
                  }}
                  disabled={loading}
                  optional
                  skipSort
                />

                <Box gridColumn="span 2">
                  <CommonSearchInput
                    id="mlb-stats-players"
                    label={label}
                    disabled={loading}
                    options={playerOptions}
                    values={
                      props.model?.playerPk
                        ? [props.model.playerPk.toString()]
                        : []
                    }
                    onChange={(v) => {
                      if (isNil(filterGlobal.season)) {
                        return;
                      }

                      const playerPk = safeNumber(first(v));

                      props.onChangeModel(
                        isNil(playerPk)
                          ? undefined
                          : seasonPlayers.find((p) => p.playerPk === playerPk)
                      );

                      if (props.autoClose) {
                        setOpen(false);
                      }
                    }}
                    strict
                    skipSort
                    optional
                  />
                </Box>
              </CommonFormGrid>
            </Box>
          </AnimateHeight>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};
